import React, { useEffect, useState } from 'react';
import { dataQualityService } from '../services/dataQualityService';
import { EMPTY_DISPLAY, TABLE_LABELS, formatFieldPairName } from '../constants/dataQuality';

const ENTITY_LABEL = { companies: 'Company', people: 'People', jobs: 'Jobs' };
const ENTITY_SINGULAR = { companies: 'Company', people: 'Person', jobs: 'Job' };

// idTooltip: the record's name (company name / person full name) + its id, so the
// ID hover identifies WHICH record the row is about — not just an opaque uuid.
function idTooltip(row, tableName) {
  const label = ENTITY_SINGULAR[tableName] || 'Record';
  const name = (row.entity_name || '').trim();
  return name
    ? `${label}: ${name}\nID: ${row.record_id}`
    : `${label} ID: ${row.record_id}`;
}

const CATEGORY = {
  derived: { label: 'Derived', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  enriched: { label: 'Enriched', badge: 'bg-teal-50 text-teal-700 border-teal-200' },
  imported: { label: 'Imported', badge: 'bg-gray-100 text-gray-500 border-gray-200' }
};

// enrichedPhrase maps an AI/discovery method to a short human sentence.
function enrichedPhrase(method) {
  switch (method) {
    case 'ai_country': return 'AI-inferred from company signals';
    case 'ai_industry_classify': return 'AI-classified from company signals';
    case 'ai_people_location': return 'AI-inferred from the person’s profile';
    case 'ai_people_linkedin': return 'AI-matched LinkedIn profile';
    case 'ai_website_discovery':
    case 'ai_website_verify': return 'AI-discovered by search';
    case 'linkedin_discovery': return 'Discovered from the website';
    case 'global_match': return 'Matched to a known company';
    default: return 'AI-inferred';
  }
}

// specTitle takes the ROW, not just the method: cctld_country reads a country off
// a domain, but WHICH domain depends on the source — a company's own website, a
// person's work-email domain, or their employer's website. Keying the wording on
// the method alone labelled every one of them "website domain", which is a false
// provenance statement in the one view whose whole job is provenance. source_field
// is the backend's human label for that source ("Website", "Email Domain",
// "Employer Website") — one of those three for every cctld_country row; the
// generic fallback covers a row whose source was never recorded.
function specTitle(row) {
  if (row.method === 'phone_cc') return 'Special extraction — phone country code';
  if (row.method === 'cctld_country') {
    return row.source_field
      ? `Special extraction — country from ${row.source_field.toLowerCase()} (ccTLD)`
      : 'Special extraction — country from a domain (ccTLD)';
  }
  return 'Notable extraction';
}

function displayValue(value) {
  if (value === null || value === undefined || value === '') return EMPTY_DISPLAY;
  return String(value);
}

// OriginCell renders the "how it came to be" story for one record.
function OriginCell({ row }) {
  const cat = CATEGORY[row.category] || CATEGORY.imported;
  let text;
  if (row.category === 'derived') {
    text = row.source_field ? (
      <>
        from <span className="font-medium text-gray-800">{row.source_field}</span>
        {row.source_value ? (
          <>{' '}<code className="font-mono text-[12px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded break-all">{row.source_value}</code></>
        ) : null}
      </>
    ) : 'Derived from your other fields';
  } else if (row.category === 'enriched') {
    // When the AI recorded what it was given, show it ("AI-inferred from
    // employer country: Thailand · email domain: acme.com") instead of a generic line.
    text = row.evidence
      ? <>AI-inferred from <span className="text-gray-800">{row.evidence}</span></>
      : enrichedPhrase(row.method);
  } else {
    text = 'As imported from your ATS';
  }
  return (
    <div className="flex items-start gap-2 flex-wrap min-w-0">
      <span className={`shrink-0 mt-px text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${cat.badge}`}>
        {cat.label}
      </span>
      {row.special ? (
        <span
          className="shrink-0 mt-px inline-flex items-center justify-center w-[17px] h-[17px] rounded-full bg-amber-500 text-white text-[11px] font-extrabold cursor-help"
          title={specTitle(row)}
        >!</span>
      ) : null}
      <span className="flex-1 min-w-0 text-gray-600 leading-normal break-words">{text}</span>
    </div>
  );
}

function ValueCell({ row }) {
  const conf = row.category === 'enriched' && typeof row.confidence === 'number'
    ? `${Math.round(row.confidence * 100)}% confidence` : null;
  return (
    <div className="text-right">
      <span className="inline-block max-w-full font-semibold text-gray-900 leading-snug break-words">
        {displayValue(row.clean_value)}
      </span>
      {conf ? <div className="mt-1 text-[11px] font-semibold text-teal-700 tabular-nums whitespace-nowrap">{conf}</div> : null}
    </div>
  );
}

export default function DataQualityDataViewerModal({
  open,
  onClose,
  runId,
  tableName,
  fieldPairName = null
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [previewMode, setPreviewMode] = useState(false); // multi-field: fell back to "all"
  const [allImported, setAllImported] = useState(false);  // single-field: nothing derived/enriched

  useEffect(() => {
    if (open) {
      setPage(1);
      setPreviewMode(false);
      setAllImported(false);
    }
  }, [open, tableName, fieldPairName]);

  useEffect(() => {
    if (!open || !runId || !tableName) return undefined;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        if (fieldPairName) {
          // Single field: show only records the platform derived or enriched.
          let result = await dataQualityService.getSamples(runId, tableName, {
            fieldPairName, sampleType: 'all', provenance: 'derived_enriched', page, pageSize: 20
          });
          let imported = false;
          if ((result?.total || 0) === 0) {
            // Nothing was inferred — fall back to the imported values so the user
            // still sees real data, with an explanatory banner.
            result = await dataQualityService.getSamples(runId, tableName, {
              fieldPairName, sampleType: 'all', provenance: '', page, pageSize: 20
            });
            imported = true;
          }
          if (!cancelled) {
            setAllImported(imported);
            setData(result);
          }
          return;
        }

        // Multi-field (per-record) view — unchanged before/after behavior.
        const sampleType = previewMode ? 'all' : 'changed';
        let result = await dataQualityService.getSamples(runId, tableName, { sampleType, page, pageSize: 20 });
        if (!previewMode && (result?.total || 0) === 0) {
          result = await dataQualityService.getSamples(runId, tableName, { sampleType: 'all', page, pageSize: 20 });
          if (!cancelled) {
            setPreviewMode(true);
            setData(result);
          }
          return;
        }
        if (!cancelled) setData(result);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Could not load sample data');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [open, runId, tableName, fieldPairName, page, previewMode]);

  if (!open) return null;

  const pageSize = data?.page_size || 20;
  const totalPages = data ? Math.max(1, Math.ceil((data.total || 0) / pageSize)) : 1;
  const entity = ENTITY_LABEL[tableName];
  const title = fieldPairName
    ? `Data viewer: ${formatFieldPairName(fieldPairName)}`
    : `Data viewer: ${TABLE_LABELS[tableName] || tableName}`;
  const subtitle = fieldPairName
    ? 'How each value came to be — the field it was derived from, or how it was enriched.'
    : (previewMode
      ? 'Preview of before and after columns (values may match when cleaning has not changed the stored text).'
      : 'Rows where the after-cleaning value differs from before cleaning.');

  const rows = data?.rows || [];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900 truncate">{title}</h3>
              {entity ? (
                <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-500 border border-gray-200 bg-gray-50 px-1.5 py-0.5 rounded">{entity}</span>
              ) : null}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-2xl leading-none px-2"
            aria-label="Close"
          >
            x
          </button>
        </div>

        {fieldPairName && allImported && !loading && !error ? (
          <div className="flex items-start gap-2 px-6 py-3 bg-gray-50 border-b border-gray-200 text-[12.5px] text-gray-600">
            <span className="shrink-0 inline-flex items-center justify-center w-[17px] h-[17px] rounded-full bg-gray-400 text-white text-[11px] font-bold mt-px">i</span>
            <span>Every value here came <span className="font-medium text-gray-700">straight from your ATS</span> — we didn’t derive or enrich it. Showing the imported values so you can see the data.</span>
          </div>
        ) : null}

        <div className="flex-1 overflow-auto px-6 py-4">
          {loading ? <p className="text-sm text-gray-500">Loading examples...</p> : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          {/* Single-field provenance view */}
          {!loading && !error && fieldPairName && rows.length > 0 ? (
            <table className="w-full table-fixed text-sm border-collapse">
              <colgroup>
                <col className="w-[6%]" />
                <col className="w-[56%]" />
                <col className="w-[38%]" />
              </colgroup>
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-200">
                  <th className="py-2 pr-2 font-semibold text-[10px] uppercase tracking-wider" />
                  <th className="py-2 pr-4 font-semibold text-[10px] uppercase tracking-wider">Origin</th>
                  <th className="py-2 pl-4 font-semibold text-[10px] uppercase tracking-wider text-right border-l border-gray-100">Value</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.record_id} className="border-b border-gray-50 align-top hover:bg-gray-50/60">
                    <td className="py-2.5 pr-2 text-center align-top">
                      <span className="group relative inline-flex">
                        <span
                          className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full border border-gray-300 text-gray-400 text-[11px] font-bold not-italic cursor-help mt-px group-hover:border-gray-500 group-hover:text-gray-700"
                          aria-label={idTooltip(row, tableName)}
                        >i</span>
                        <span
                          role="tooltip"
                          className="pointer-events-none absolute left-6 top-1/2 -translate-y-1/2 z-[120] hidden group-hover:block whitespace-nowrap rounded-md bg-gray-900 text-left px-2.5 py-1.5 shadow-lg"
                        >
                          {row.entity_name ? (
                            <span className="block text-[11.5px] font-medium text-white">
                              {(ENTITY_SINGULAR[tableName] || 'Record')}: {row.entity_name}
                            </span>
                          ) : null}
                          <span className="block text-[10px] font-mono text-gray-300">ID: {row.record_id}</span>
                        </span>
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 align-top"><OriginCell row={row} /></td>
                    <td className="py-2.5 pl-4 align-top border-l border-gray-100"><ValueCell row={row} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          {/* Multi-field (per-record) view — unchanged */}
          {!loading && !error && !fieldPairName && rows.length > 0 ? (
            <table className="w-full table-fixed text-xs border-collapse">
              <colgroup>
                <col className="w-[26%]" />
                <col className="w-[37%]" />
                <col className="w-[37%]" />
              </colgroup>
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4 font-medium">Field</th>
                  <th className="py-2 pr-4 font-medium">Before cleaning</th>
                  <th className="py-2 pr-4 font-medium">After cleaning</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <React.Fragment key={row.record_id}>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <td colSpan={3} className="py-2 pr-4 text-[11px] font-mono text-gray-600 break-all">
                        {row.record_id}
                      </td>
                    </tr>
                    {row.pairs.map((pair) => (
                      <tr key={`${row.record_id}-${pair.field_pair_name}`} className="border-b border-gray-100 align-top">
                        <td className="py-2 pr-4 font-medium text-gray-900 align-top">
                          {formatFieldPairName(pair.field_pair_name)}
                        </td>
                        <td className="py-2 pr-4 text-gray-700 whitespace-pre-wrap break-words align-top">
                          {displayValue(pair.stage_value)}
                        </td>
                        <td className="py-2 pr-4 text-gray-900 whitespace-pre-wrap break-words align-top">
                          {displayValue(pair.clean_value)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          ) : null}

          {!loading && !error && rows.length === 0 ? (
            <p className="text-sm text-gray-500">No records found for this selection.</p>
          ) : null}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            {data?.total != null ? `${Number(data.total).toLocaleString()} ${allImported ? 'imported' : 'matching'} records` : ''}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={loading || page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600 self-center">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={loading || page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
